import ContactProfileEditor from 'components/ContactProfileEditor'
import { businessPath } from 'utils/businessRoutes'
import React, { useCallback, useState, useRef, useEffect, memo } from 'react'
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
} from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useSnackbar } from 'notistack'
import * as uiActions from 'modules/UI/actions'
import { fetchBusinesses } from 'modules/User/actions'
import * as businessActions from 'modules/Business/actions'
import api from 'utils/api'
import CategorySelector from './components/CategorySelector'
import BusinessLogoUpload from './components/BusinessLogoUpload'
import { styled } from '@mui/system'
import * as userActions from 'modules/User/actions'
import AddressFormFields from 'components/address/AddressFormFields'
import { DEFAULT_COUNTRY_CODE, getCountryName } from 'components/address/countries'
import { emptyAddressFormValues } from 'components/address/mapPlaceToFormValues'

// ---------------- Styled Components ----------------
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: '#f9f9f9',
}))

const StyledFormTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#e0e0e0',
  padding: theme.spacing(2),
  textAlign: 'center',
}))

const StyledFormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '8px',
  backgroundColor: '#ffffff',
}))

const StyledFormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}))

const StyledBlurBackground = styled('div')(({ theme }) => ({
  filter: 'blur(0px)',
  transition: 'filter 0.3s',
}))

// ---------------- Phone Formatting ----------------
const formatPhone = (input = "") => {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  let formatted = "";
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};

const isValidPhone = phone => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

// ---------------- Validation Schema ----------------
const urlPattern =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/

yup.addMethod(yup.mixed, 'defined', function () {
  return this.test('defined', '{path} is required', (value) => value !== undefined)
})

const schema = yup.object().shape({
  companyName: yup.string().required('Name is required'),
  street: yup.string().required('Street is required'),
  addressLine2: yup.string().optional(),
  city: yup.string().required('City is required'),
  zipCode: yup.string().required('Postal code is required'),
  countryCode: yup.string().required('Country is required'),
  countryName: yup.string().optional(),
  latitude: yup.number().nullable().optional(),
  longitude: yup.number().nullable().optional(),
  state: yup.string().when('countryCode', {
    is: DEFAULT_COUNTRY_CODE,
    then: (schema) => schema.required('State is required'),
    otherwise: (schema) => schema.optional(),
  }),
  businessPhone: yup
    .string()
    .test('phone-format', 'Phone must be blank or in format (212) 555-1234', function(value) {
      if (!value || value.trim() === '') return true; // Optional field
      return isValidPhone(value);
    })
    .optional(),
  siteUrl: yup
    .string()
    .matches(urlPattern, 'Url is not valid')
    .max(240, 'Too many characters')
    .required('Site url is required'),
  logoUrl: yup.string().required('Logo is required'),
  categories: yup.mixed().required('Pick a category'),
  fulfillment: yup
    .string()
    .oneOf(['RETAIL', 'SERVICE', 'RETAIL_AND_SERVICE'], 'Select a fulfillment type')
    .required('Fulfillment is required'),
  jobTitle: yup.string().max(255, 'Too many characters').optional(),
})

const BusinessForm = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = useSelector((s) => s.ui.showBusinessForm)
  const business = useSelector((s) => s.business)
  const userDetails = useSelector((s) => s.user?.details)
  const readOnly = userDetails?.lifecycleStatus === 'SUSPENDED' || (isEdit === 1 && ['SUSPENDED', 'ARCHIVED'].includes(business?.lifecycleStatus))

  const needsAccount = isEdit !== 1 && !userDetails?.businessAccount;
  const [sameAccountName, setSameAccountName] = useState(true)
  const [accountName, setAccountName] = useState('')
  const [accountNameError, setAccountNameError] = useState('')
  const [titleError, setTitleError] = useState('')
  const [savedProfile, setSavedProfile] = useState(null)

  const [logoUploadSuccess, setLogoUploadSuccess] = useState(false)
  const [urlValidationError, setUrlValidationError] = useState(null)
  const [isValidatingUrl, setIsValidatingUrl] = useState(false)
  const firstInputRef = useRef(null)
  const submissionInFlight = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // ---------------- Focus Fix ----------------
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus()
    }
  }, [isEdit]) // only when dialog open or mode changes


  const biz = business.businessId
    ? {
        categories: business.categories,
        companyName: business.companyInfo.companyName,
        street: business.companyInfo.street,
        addressLine2: business.companyInfo.addressLine2 || '',
        zipCode: business.companyInfo.zipCode,
        city: business.companyInfo.city,
        countryCode: business.companyInfo.countryCode || DEFAULT_COUNTRY_CODE,
        countryName: business.companyInfo.countryName || getCountryName(business.companyInfo.countryCode || DEFAULT_COUNTRY_CODE),
        latitude: business.companyInfo.latitude ?? null,
        longitude: business.companyInfo.longitude ?? null,
        state:
          business.companyInfo.state !== null
            ? business.companyInfo.state.toUpperCase()
            : business.companyInfo.state,
        siteUrl: business.companyInfo.siteUrl,
        logoUrl: business.companyInfo.logoUrl,
        fulfillment: business.companyInfo.fulfillment || '',
        businessPhone: business.companyInfo.phoneNumber ? formatPhone(business.companyInfo.phoneNumber) : '',
        jobTitle: business.userInfo?.job || '',
      }
    : {}

  const initialDefaults =
    isEdit === 1
      ? biz
      : {
          ...emptyAddressFormValues(),
          fulfillment: '',
          jobTitle: '',
        }

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: initialDefaults,
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    const addressFieldNames = [
      'street',
      'addressLine2',
      'city',
      'state',
      'zipCode',
      'countryCode',
      'countryName',
      'latitude',
      'longitude',
    ]
    addressFieldNames.forEach((name) => register(name))
  }, [register])

  const handleClose = useCallback(() => {
    if (submissionInFlight.current) return
    dispatch(uiActions.hideBusinessForm())
  }, [dispatch])

  const handleLogoUploadSuccess = () => setLogoUploadSuccess(true)

  const handleAddressFormChange = (patch) => {
    Object.entries(patch).forEach(([field, value]) => {
      setValue(field, value, { shouldValidate: true })
    })
  }

  const addressFormValues = {
    street: watch('street') || '',
    addressLine2: watch('addressLine2') || '',
    city: watch('city') || '',
    state: watch('state') || '',
    zipCode: watch('zipCode') || '',
    countryCode: watch('countryCode') || '',
    countryName: watch('countryName') || '',
    latitude: watch('latitude'),
    longitude: watch('longitude'),
  }

  const addressFormErrors = {
    street: errors.street?.message,
    addressLine2: errors.addressLine2?.message,
    city: errors.city?.message,
    state: errors.state?.message,
    zipCode: errors.zipCode?.message,
    countryCode: errors.countryCode?.message,
  }

  const validateUrlWithGoogleSafeBrowsing = async (url) => {
    if (!url || url.trim() === '') {
      setUrlValidationError(null)
      return true
    }

    setIsValidatingUrl(true)
    setUrlValidationError(null)

    try {
      const response = await api.post('/common/validate-url', { url })
      
      if (response.status === 'BLOCK') {
        setUrlValidationError(response.message || 'This URL cannot be used for security reasons.')
        setIsValidatingUrl(false)
        return false
      } else {
        setUrlValidationError(null)
        setIsValidatingUrl(false)
        return true
      }
    } catch (error) {
      // Fail closed - block on API errors
      console.error('URL validation error:', error)
      setUrlValidationError('This URL cannot be used for security reasons.')
      setIsValidatingUrl(false)
      return false
    }
  }

  const handleUrlBlur = async (e) => {
    const url = e.target.value
    // Validate URL when user moves focus away from the field
    if (url && url.trim() !== '') {
      await validateUrlWithGoogleSafeBrowsing(url)
    } else {
      setUrlValidationError(null)
    }
  }

  const handleFormSubmit = async ({ categories, businessPhone, jobTitle, ...companyInfo }) => {
    if (readOnly || submissionInFlight.current) return
    if (needsAccount && !profileComplete) return
    if (isContactOwner && (!jobTitle?.trim() || jobTitle.trim().length > 255)) {
      setTitleError('Enter a job title of 1 to 255 characters for this business.')
      return
    }
    setTitleError('')
    if (needsAccount && !sameAccountName && (!accountName.trim() || accountName.trim().length > 255)) {
      setAccountNameError('Enter an account name of 1 to 255 characters.')
      return
    }
    setAccountNameError('')
    submissionInFlight.current = true
    setIsSubmitting(true)

    try {
      const siteUrl = companyInfo.siteUrl
      if (siteUrl && siteUrl.trim() !== '') {
        const isValid = await validateUrlWithGoogleSafeBrowsing(siteUrl)
        if (!isValid) {
          enqueueSnackbar('Please enter a valid and safe URL', { variant: 'error' })
          return
        }
      }

      const businessId = isEdit === 1 ? business.businessId : null
      const phoneNumber = businessPhone ? businessPhone.replace(/\D/g, '') : null
      const payload = {
        businessId,
        ...(isContactOwner ? { jobTitle: jobTitle.trim() } : {}),
        ...(needsAccount ? { accountName: sameAccountName ? companyInfo.companyName : accountName.trim() } : {}),
        companyInfo: { ...companyInfo, businessId, phoneNumber: phoneNumber || null },
        categories: categories.map((c) => c.categoryId),
      }

      let savedBusinessId
      try {
        savedBusinessId = await api.post('/business', payload)
        if (!savedBusinessId || savedBusinessId.error) {
          throw new Error('Business response did not contain an ID')
        }
      } catch (error) {
        enqueueSnackbar('Could not save your business. Check your business list before trying again.', { variant: 'error' })
        return
      }

      dispatch(userActions.fetchUser())
      if (isEdit === 1) {
        dispatch(businessActions.updateBusinessName(business.businessId, companyInfo.companyName))
        dispatch(businessActions.fetchBusiness(business.businessId, true))
      } else {
        try {
          const created = await api.get(`/business/${savedBusinessId}`)
          navigate(businessPath(savedBusinessId, created.organizationId))
        } catch {
          navigate('/business')
          enqueueSnackbar('Your business was saved. Open My business to continue.', { variant: 'warning' })
        }
        enqueueSnackbar('Congratulations on creating your business, now let\'s create your first offer.', { variant: 'success' })
      }
      dispatch(uiActions.hideBusinessForm())
      api.get('/user/businesses/')
        .then((res) => dispatch(fetchBusinesses(res.content)))
        .catch(() => enqueueSnackbar('Your business was saved. Refresh the page to update your business list.', { variant: 'warning' }))
    } finally {
      submissionInFlight.current = false
      setIsSubmitting(false)
    }
  }

  const isContactOwner = isEdit !== 1 || String(business.ownerUserId) === String(userDetails?.userId)
  const contact = isEdit === 1
    ? { ...business.userInfo, ...(isContactOwner ? savedProfile : {}) }
    : { ...userDetails, ...savedProfile }
  const profileComplete = [contact?.firstName, contact?.lastName, contact?.phone]
    .every(value => value?.trim())
  // Get register props for siteUrl and merge onBlur handlers
  const siteUrlRegister = register('siteUrl')
  const { onBlur: registerOnBlur, ...siteUrlRegisterProps } = siteUrlRegister

  const primaryContactCard = <Card variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'primary.main' }}>
    <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography variant="subtitle1">Primary contact</Typography>
      <Chip size="small" color="primary" label="Selected" />
    </Grid>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {isContactOwner
        ? 'Your profile supplies the name, email, and phone for this business contact.'
        : "The account owner's profile supplies the name, email, and phone for this business contact."}
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <ContactProfileEditor contact={contact} editable={isContactOwner} disabled={readOnly || isSubmitting}
          requireComplete={needsAccount} onSaved={setSavedProfile} />
      </Grid>
      <Grid item xs={12} md={4}>
        <Box aria-hidden="true" sx={{ display: { xs: 'none', md: 'block' }, height: 32 }} />
        {isContactOwner ? <TextField fullWidth required size="small" label="Job title for this business"
          error={Boolean(titleError)} helperText={titleError || 'This title applies only to this business.'}
          inputProps={{ maxLength: 255 }} {...register('jobTitle')} />
          : <><Typography fontWeight={600}>Job title for this business</Typography>
            <Typography variant="body2">{contact?.job || 'Not provided'}</Typography></>}
      </Grid>
    </Grid>
  </Card>

  // ---------------- Render ----------------
  return (
    <StyledBlurBackground>
      <Dialog open fullWidth maxWidth="md" onClose={handleClose}>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate autoComplete="off">
          <StyledFormTitle>
            <Typography variant="h5" component="span">
              {isEdit === 1 ? 'Edit business' : 'Create new business'}
            </Typography>
          </StyledFormTitle>

          <StyledDialogContent>
            <StyledFormPaper elevation={3}>
              {/* Business Name */}
              <StyledFormField>
                <FormControl fullWidth variant="outlined" error={!!errors.companyName}>
                  <TextField
                    inputRef={firstInputRef} // ✅ focus only once on open
                    fullWidth
                    label="Business name"
                    margin="dense"
                    {...register('companyName')}
                    variant="outlined"
                    autoComplete="new-password"
                  />
                  {errors.companyName && (
                    <FormHelperText>{errors.companyName.message}</FormHelperText>
                  )}
                </FormControl>
                {needsAccount && <>
                  <FormControlLabel
                    control={<Checkbox checked={sameAccountName} onChange={event => setSameAccountName(event.target.checked)} />}
                    label="Use the business name as the account name"
                  />
                  {!sameAccountName && <TextField fullWidth required label="Account name" value={accountName}
                    onChange={event => setAccountName(event.target.value)} inputProps={{ maxLength: 255 }}
                    error={Boolean(accountNameError)} helperText={accountNameError} />}
                  <Typography variant="body2" color="text.secondary">
                    Your business account can manage multiple businesses. Its address will initially match this business.
                    You can make changes later on the Account page.
                  </Typography>
                </>}
                {isEdit !== 1 && userDetails?.businessAccount && <Typography variant="body2" color="text.secondary">
                  Account: {userDetails.businessAccount.name}
                </Typography>}
              </StyledFormField>

              {/* Address */}
              <StyledFormField>
                <AddressFormFields
                  values={addressFormValues}
                  onChange={handleAddressFormChange}
                  errors={addressFormErrors}
                  searchLabel="Search business address"
                  fieldSize="medium"
                  fieldMargin="dense"
                />
              </StyledFormField>

              {/* Phone (Optional) and Site URL */}
              <StyledFormField>
                <Grid container spacing={2} alignItems="flex-start">
                  {/* Phone (Optional) */}
                  <Grid item xs={12} sm={5}>
                    <Controller
                      name="businessPhone"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth variant="outlined" error={!!errors.businessPhone}>
                          <TextField
                            fullWidth
                            label="Phone (optional)"
                            margin="dense"
                            type="tel"
                            {...field}
                            onChange={(e) => {
                              const formatted = formatPhone(e.target.value);
                              field.onChange(formatted);
                            }}
                            autoComplete="new-password"
                            helperText={errors.businessPhone?.message || 'Business number will be available in your offer.'}
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* Site URL */}
                  <Grid item xs={12} sm={7}>
                    <FormControl fullWidth variant="outlined" error={!!errors.siteUrl || !!urlValidationError}>
                      <TextField
                        fullWidth
                        label="Site URL"
                        margin="dense"
                        {...siteUrlRegisterProps}
                        onBlur={(e) => {
                          // Call react-hook-form's onBlur handler first
                          registerOnBlur(e)
                          // Then call our custom validation
                          handleUrlBlur(e)
                        }}
                        autoComplete="new-password"
                        disabled={isValidatingUrl}
                        helperText={isValidatingUrl ? 'Validating URL...' : undefined}
                      />
                      {errors.siteUrl && (
                        <FormHelperText>{errors.siteUrl.message}</FormHelperText>
                      )}
                      {urlValidationError && !errors.siteUrl && (
                        <FormHelperText>{urlValidationError}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </StyledFormField>

              {/* Logo Upload with Preview */}
              <StyledFormField sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="stretch">
                  {/* Logo Upload - Same width as phone field above */}
                  <Grid item xs={12} sm={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <BusinessLogoUpload
                      control={control}
                      error={errors.logoUrl}
                      placeholder="Business logo"
                      name="logoUrl"
                      uploadUrl={`/api/business/saveImage`}
                      onSuccess={handleLogoUploadSuccess}
                      helperText="Drag and drop, or upload your logo. Choose an image up to 2 MB. We’ll optimize it before uploading."
                    />
                    <Box sx={{ mt: { xs: 2, sm: 'auto' }, pt: { sm: 2 } }}>
                      <Controller
                        name="fulfillment"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value || ''}
                            select
                            fullWidth
                            label="Fulfillment"
                            error={!!errors.fulfillment}
                            helperText={
                              errors.fulfillment?.message ||
                              'Select the type of fulfillment you provide your customers.'
                            }
                          >
                            <MenuItem value="RETAIL">Retail</MenuItem>
                            <MenuItem value="SERVICE">Service</MenuItem>
                            <MenuItem value="RETAIL_AND_SERVICE">Retail and service</MenuItem>
                          </TextField>
                        )}
                      />
                    </Box>
                  </Grid>
                  {/* Image Preview */}
                  <Grid item xs={12} sm={7}>
                    {watch('logoUrl') && (
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          border: '1px solid #e0e0e0',
                          borderRadius: 1,
                          p: 2,
                          bgcolor: '#f9f9f9',
                          minHeight: 100,
                        }}
                      >
                        <img
                          src={watch('logoUrl')}
                          alt="Logo preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 200,
                            objectFit: 'contain',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </StyledFormField>

              {/* Category */}
              <StyledFormField>
                <FormControl fullWidth variant="outlined" error={!!errors.categories}>
                  <CategorySelector control={control} size="medium" />
                  {errors.categories && (
                    <FormHelperText>{errors.categories.message}</FormHelperText>
                  )}
                </FormControl>
              </StyledFormField>

              {primaryContactCard}
            </StyledFormPaper>
          </StyledDialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="secondary" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={readOnly || isSubmitting}>
              {isSubmitting ? 'Saving…' : isEdit === 1 ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </StyledBlurBackground>
  )
}

export default memo(BusinessForm)
